import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear stored credentials
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on the login page
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  register: (userData) => api.post('/auth/register', userData),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  getEmailNotifications: () => api.get('/auth/email-notifications'),
  updateEmailNotifications: (data) => api.put('/auth/email-notifications', data)
};

// Users API
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, data) => api.patch(`/users/${id}/password`, data),
  toggleActive: (id) => api.patch(`/users/${id}/toggle-active`)
};

// Materials API
export const materialsAPI = {
  getAll: (params) => api.get('/materials', { params }),
  getAllSortedByFrequency: () => api.get('/materials', { params: { sortByFrequency: 'true' } }),
  search: (q) => api.get('/materials/search', { params: { q } }),
  getByBarcode: (barcode) => api.get(`/materials/barcode/${barcode}`),
  getById: (id) => api.get(`/materials/${id}`),
  create: (data) => api.post('/materials', data),
  update: (id, data) => api.put(`/materials/${id}`, data),
  delete: (id) => api.delete(`/materials/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/materials/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  export: () => api.get('/materials/export', { responseType: 'blob' }),
  getPriceHistory: (id) => api.get(`/materials/${id}/price-history`)
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  updateStatus: (id, data) => api.patch(`/orders/${id}/status`, data),
  delete: (id) => api.delete(`/orders/${id}`)
};

// Shipping API
export const shippingAPI = {
  getAll: (params) => api.get('/shipping', { params }),
  getDelivery: (orderId) => api.get(`/shipping/${orderId}`),
  createOrUpdate: (orderId, data) => api.post(`/shipping/${orderId}`, data),
  updateStatus: (orderId, data) => api.patch(`/shipping/${orderId}/status`, data),
  delete: (orderId) => api.delete(`/shipping/${orderId}`)
};

// Reports API
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getHeadquarters: (params) => api.get('/reports/headquarters', { params })
};

// VAT API
export const vatAPI = {
  get: () => api.get('/vat'),
  update: (data) => api.put('/vat', data)
};

// Stock API
export const stockAPI = {
  recordIntake: (data) => api.post('/stock/intake', data),
  recordOuttake: (data) => api.post('/stock/outtake', data),
  undoTransaction: (transactionId) => api.delete(`/stock/transactions/${transactionId}`),
  getTransactions: (params) => api.get('/stock/transactions', { params }),
  getMaterialTransactions: (materialId) => api.get(`/stock/materials/${materialId}/transactions`),
  adjustStock: (data) => api.post('/stock/adjust', data),
  getLowStock: () => api.get('/stock/low-stock'),
  getCategories: () => api.get('/stock/categories'),
  getMonitoring: (params) => api.get('/stock/monitoring', { params })
};

// Supplier API
export const supplierAPI = {
  getAll: () => api.get('/suppliers'),
  getById: (id) => api.get(`/suppliers/${id}`),
  search: (q) => api.get('/suppliers/search', { params: { q } }),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`)
};

// Buyer API
export const buyerAPI = {
  getAll: () => api.get('/buyers'),
  getById: (id) => api.get(`/buyers/${id}`),
  search: (q) => api.get('/buyers/search', { params: { q } }),
  create: (data) => api.post('/buyers', data),
  update: (id, data) => api.put(`/buyers/${id}`, data),
  delete: (id) => api.delete(`/buyers/${id}`)
};

// Fulfillment API
export const fulfillmentAPI = {
  getReadyOrders: (params) => api.get('/fulfillment/ready', { params }),
  startPicking: (orderId) => api.post(`/fulfillment/start/${orderId}`),
  scanItem: (data) => api.post('/fulfillment/scan', data),
  completePicking: (orderId) => api.post(`/fulfillment/complete/${orderId}`),
  revertToPicking: (orderId) => api.post(`/fulfillment/revert/${orderId}`),
  getProgress: (orderId) => api.get(`/fulfillment/${orderId}`),
  getFulfillmentDetails: (orderId) => api.get(`/fulfillment/details/${orderId}`),
  removeItem: (itemId, orderId) => api.delete(`/fulfillment/item/${itemId}`, { data: { orderId } }),
  addItem: (data) => api.post('/fulfillment/add-item', data)
};

// Recipes API
export const recipesAPI = {
  getAll: (params) => api.get('/recipes', { params }),
  getById: (id) => api.get(`/recipes/${id}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  export: (id) => api.get(`/recipes/${id}/export`, { responseType: 'blob' })
};

// QSC Assessment API
export const qscAPI = {
  // Categories
  getCategories: () => api.get('/qsc/categories'),
  createCategory: (data) => api.post('/qsc/categories', data),
  updateCategory: (id, data) => api.put(`/qsc/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/qsc/categories/${id}`),

  // Subcategories
  createSubcategory: (catId, data) => api.post(`/qsc/categories/${catId}/subcategories`, data),
  updateSubcategory: (subId, data) => api.put(`/qsc/subcategories/${subId}`, data),
  deleteSubcategory: (subId) => api.delete(`/qsc/subcategories/${subId}`),

  // Items
  createItem: (catId, data) => api.post(`/qsc/categories/${catId}/items`, data),
  updateItem: (itemId, data) => api.put(`/qsc/items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/qsc/items/${itemId}`),

  // Assessments
  getAssessments: () => api.get('/qsc'),
  getAssessment: (id) => api.get(`/qsc/${id}`),
  createAssessment: (data) => api.post('/qsc', data),
  completeAssessment: (id) => api.patch(`/qsc/${id}/complete`),
  deleteAssessment: (id) => api.delete(`/qsc/${id}`),

  // Improvements
  getImprovements: () => api.get('/qsc/improvements'),
  submitImprovement: (data) => api.post('/qsc/improvements', data),
  reviewImprovement: (id, data) => api.patch(`/qsc/improvements/${id}/review`, data),
  uploadImprovementPhotos: (id, formData) =>
    api.post(`/qsc/improvements/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  uploadAssessmentItemPhotos: (assessmentId, itemId, formData) =>
    api.post(`/qsc/${assessmentId}/items/${itemId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all')
};

export default api;
